/**
 * useCategories — ViewModel for /manage/categories.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns CRUD mutations.
 *
 * Mirrors src/viewmodels/useUserTypes.ts in shape (M:N picker
 * payload — attribute_ids array on each row, attributes catalogue
 * via the list's `extras`).
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { AttributeRef, CategoryDto } from '../models/category.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/category.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { AttributeRef, CategoryDto } from '../models/category.model'

export interface CreateCategoryInput {
  readonly name: string
  readonly description: string | null
  readonly attribute_ids: string[]
}

export interface UpdateCategoryInput {
  readonly name?: string
  readonly description?: string | null
  readonly attribute_ids?: string[]
}

export interface UseCategoriesReturn {
  readonly items: CategoryDto[]
  readonly attributes: AttributeRef[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateCategoryInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateCategoryInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: CategoryDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly attributes: AttributeRef[]
  }
}

export function useCategories(): UseCategoriesReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<CategoryDto, AttributeRef[]>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGECATEGORIES}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: body.data.attributes
        }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateCategoryInput) => {
      const r = await mutateJson<CategoryDto>(
        authedFetch,
        APIROUTES.MANAGECATEGORIES,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateCategoryInput) => {
      const r = await mutateJson<CategoryDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGECATEGORYITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGECATEGORYITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    attributes: paged.extras ?? [],
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
